import React, { useContext, useMemo, useState } from "react";
import { Table } from "antd";
import style from "./index.module.scss";
import TableTitle from "./Components/TableTitle";
import { ColumnProps } from "antd/lib/table/interface";
import RenderColumn from "./Components/RenderColumn";
import SettingModal from "./Components/SettingModal";
import useRequest from "utils/useRequest";
import {
    GradeItemProps,
    PublishStatus,
    queryDcGradeDecimalSet,
    queryGrade
} from "service/score";
import Tools from "../Tools";
import useModalHandler from "../../../../../../utils/v2/useModalHandler";
import { isTrue } from "service/service";
import { PaginationConfig } from "antd/es/pagination";
import { useAtom } from "jotai";
import { countAtom } from "../../../store";
import ScoreBaseContext from "../../../context";

interface TableProps {
    courseNo: string;
    courseSeq: string;
    status: PublishStatus | null; //成绩单状态
    publishLoading: boolean;
}
const Index = ({ courseNo, courseSeq, status, publishLoading }: TableProps) => {
    const modal = useModalHandler(SettingModal);

    const [pageSize, setPageSize] = useState<number | undefined>(10); //每页条数
    const [pageNo, setPageNo] = useState<number | undefined>(1); //当前页
    const [keyword, setKeyword] = useState("");
    const [dataSource, setDataSource] = useState<
        Record<string, GradeItemProps>[]
    >([]);

    const [count] = useAtom(countAtom);

    const [data, { loading }, updateData] = useRequest(
        async () => {
            const data = await queryGrade({
                courseNo,
                courseSeq,
                keyword,
                pageNo,
                pageSize
            });
            setDataSource(data?.dataSource);
            return data;
        },
        {},
        [keyword, pageSize, pageNo, count]
    );

    const [config, {}, refreshConfig] = useRequest(
        () => queryDcGradeDecimalSet(courseNo, courseSeq),
        {},
        [courseNo, courseSeq]
    );

    function openModal(source: Record<string, GradeItemProps>) {
        const newSource = { ...source, courseNo, courseSeq };
        modal.open();
        modal.setPayload(newSource);
    }

    const { ShowType } = useContext(ScoreBaseContext);

    const columns: ColumnProps<Record<string, GradeItemProps>>[] =
        useMemo(() => {
            return (
                data?.columns?.map(item => {
                    return {
                        ...item,
                        fixed: ["no", "studentNo", "studentName"].includes(
                            item.dataIndex
                        )
                            ? "left"
                            : false,
                        align: "center",
                        title: (
                            <TableTitle
                                title={item.title}
                                dataIndex={item.dataIndex}
                                weight={item.weight}
                                isCustom={item.isCustom === "0"}
                                updateData={updateData}
                                config={config}
                                refreshConfig={refreshConfig}
                            />
                        ),
                        render: (
                            text: GradeItemProps,
                            record: Record<string, GradeItemProps>,
                            index: number
                        ) => {
                            return (
                                <RenderColumn
                                    text={text}
                                    ShowType={ShowType}
                                    record={record}
                                    index={index}
                                    item={item}
                                    dataSource={dataSource}
                                    onOpen={openModal}
                                    courseNo={courseNo}
                                    courseSeq={courseSeq}
                                    status={status}
                                    updateData={updateData}
                                    setDataSource={setDataSource}
                                />
                            );
                        }
                    };
                }) || []
            );
        }, [data?.columns, config, ShowType]);

    const pagination = {
        current: pageNo,
        total: data?.totalSize || 0,
        pageSize: pageSize,
        pageSizeOptions: ["10", "20", "50", "100"],
        showSizeChanger: true,
        showTotal: (total: number) => <span>总计{total}条</span>
    };

    const onTableChange = (pagination: PaginationConfig) => {
        setPageNo(pagination?.current);
        setPageSize(pagination?.pageSize);
    };

    return (
        <div className={style.root}>
            <Tools
                keyword={keyword}
                setKeyword={setKeyword}
                setPageNo={setPageNo}
                courseNo={courseNo}
                courseSeq={courseSeq}
                refreshData={updateData}
                hasCustom={
                    data?.columns?.some(item => item.isCustom === "0") || false
                }
                status={status}
            />
            <div className={style.container}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    rowKey="studentNo"
                    className={style.table}
                    loading={loading}
                    pagination={pagination}
                    scroll={{ x: "max-content" }}
                    onChange={onTableChange}
                />
                {isTrue(status?.hasPublished) &&
                    !publishLoading &&
                    !loading && <div className={style.publishIcon}>已发布</div>}
            </div>

            {modal.render(
                <SettingModal
                    visible={modal.visible}
                    onCancel={modal.close}
                    onSuccess={updateData}
                />
            )}
        </div>
    );
};

export default Index;
